import { useEffect, useMemo, useRef, useState, useCallback, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, COLORS, Icon, ICONS, Modal, Switch, Tab } from "abs-ui-components";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";
import MpParticulars from "./MpParticulars/MpParticulars";
import BasicData from "./BasicData/BasicData";
import DataGaps from "./DataGaps/DataGaps";
import Management from "./Management/Management";
import FurtherInformation from "./FurtherInformation/FurtherInformation";
import ActivityData from "./ActivityData/ActivityData";
import dayjs from "dayjs";
import { callApi, getMonitoringPlanFlags, getVesselById, useMonitoringPlanActions } from "../../../services/MonitoringPlanService";
import { API_ROUTES } from "./ApiRoutes/ApiRoutes";
import { createB1Payload } from "./Constants";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { tabs } from "./Constants";
import CloneModal from "../CloneModal";
import ResultSummaryModal from "../ResultSummaryModal";
import { FeatureType } from "../../../constants/FeatureToggles";
import { ClaimsContext } from "../../../services/ClaimsService";

const MrvMonitoringPlan = ({ vesselMstId }) => {
    const [selectedTab, setActiveTab] = useState("MP PARTICULARS");
    const [monitoringData, setMonitoringData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allDropdownData, setAllDropdownData] = useState(null);
    const [mode, setMode] = useState("");
    const [onCreateVesselData, setOnCreateVesselData] = useState(null);
    const hasAutoSubmitted = useRef(false);
    const [hasMrvPlan, setHasMrvPlan] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [cloneType, setCloneType] = useState("");
    const [selectedSections, setSelectedSections] = useState([]);
    const [selectedVessels, setSelectedVessels] = useState([]);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [sectionsList, setSectionsList] = useState([]);
    const [showResultModal, setShowResultModal] = useState(false);
    const [resultSummaryData, setResultSummaryData] = useState([]);
    const { userHasFeature } = useContext(ClaimsContext)

    useEffect(() => {
        const flagCheck = async () => {
            const flagsResponse = await getMonitoringPlanFlags(vesselMstId);
            const flags = flagsResponse?.data;
            if (flags) {
                setHasMrvPlan(flags.hasMrvPlan);
                setMode(flags.hasMrvPlan ? "Edit" : "Create");
            }
        }
        flagCheck();
    }, [])

    useEffect(() => {
        const fetchMonitoringDropdownData = async () => {
            try {
                const dropdownData = await callApi({
                    method: "GET",
                    url: API_ROUTES.GET_ALL_DROPDOWN_DATA(),
                    data: {},
                });
                if (dropdownData) {
                    setAllDropdownData(dropdownData);
                }
            } catch (error) {
                console.error("Error fetching monitoring plan data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchMonitoringDropdownData();
    }, []);

    const fetchMonitoringData = useCallback(async () => {
        try {
            const response = await callApi({
                method: "GET",
                url: API_ROUTES.GET_MRV_DETAILS_BY_VESSELID(vesselMstId),
                data: {},
            });
            if (response) {
                setMonitoringData(response);
            }
        } catch (error) {
            console.error("Error fetching monitoring plan data", error);
        } finally {
            setLoading(false);
        }
    });

    const handleValidate = async (planType) => {
        try {
            const res = await callApi({
                method: "POST",
                url: API_ROUTES.Validate_Plan("Mrv", vesselMstId),
                data: {}
            });

            if (res?.isValid) {
                await fetchSections();
                emitToasterMsg("success", `EU MRV MP Clone`, "Validate successfully");
                setCloneType(planType);
                setShowCloneModal(true);
            }
            else {
                emitToasterMsg("error", `EU MRV MP Clone`, res?.errors || "Validate failed");
            }
        } catch (err) {
            console.log(err, "error::")
            emitToasterMsg("error", `EU MRV MP Clone`, err.errors || "Validate failed");
        }
    }

    const fetchSections = async () => {
        try {
            const res = await callApi({
                method: "GET",
                url: API_ROUTES.Get_Sections_By_PlanType("Mrv")
            });
            // Store in your modal's list state
            setSectionsList(res.sections);

        } catch (error) {
            console.error("Failed to load sections", error);
            emitToasterMsg("error", "Sections", "Failed to load sections");
        }
    };

    useEffect(() => {
        if (mode === "Edit" && vesselMstId) {
            fetchMonitoringData();
        }
    }, [mode, vesselMstId]);

    useEffect(() => {
        if (hasMrvPlan) return;
        else {
            (async () => {
                try {
                    const data = await getVesselById(vesselMstId);
                    setOnCreateVesselData(data);

                } catch {

                }
            })();
        }
    }, [hasMrvPlan]);

    const handleSubmit = async (rawData, withToast = false) => {
        const payload = createB1Payload(rawData, "Create", allDropdownData);
        const finalPayload =
        {
            ...payload,
        };
        try {
            const res = await callApi({
                method: "POST",
                url: API_ROUTES.B1(vesselMstId),
                data: finalPayload,
            });
            if (res?.isSuccess) {
                if (typeof fetchMonitoringData === "function") {
                    await fetchMonitoringData();
                }
            }
        } catch (err) {
            console.error("API Error:", err.errorMessage);
        }
    };

    /* 3. Auto‑submit exactly once in Create mode */
    useEffect(() => {
        if (
            mode === "Create" &&
            vesselMstId &&
            onCreateVesselData &&
            !hasAutoSubmitted.current
        ) {
            hasAutoSubmitted.current = true;
            handleSubmit(onCreateVesselData, false);
        }
    }, [mode, vesselMstId, onCreateVesselData]);

    const hasAutoSavedPartA = useRef(false);

    const autoCreatePartA = useCallback(async () => {
        if (!vesselMstId) return;
        const payload = {
            vesselMstId: Number(vesselMstId),
            createdDate: dayjs()?.toISOString(),
            status: "Created",
            version: 0,
            isActive: true,
        };
        try {
            const res = await callApi({
                method: "POST",
                url: API_ROUTES.A,
                data: payload,
            });
            if (res?.isSuccess) {
                const flagsResponse = await getMonitoringPlanFlags(vesselMstId);
                const flags = flagsResponse?.data;
                // Set only the one you need
                if (flags) {
                    setHasMrvPlan(flags.hasMrvPlan);
                }
                await fetchMonitoringData();
            }
        } catch (err) {
            console.error("Auto‑save Part‑A failed:", err.errorMessage);
        }
    }, [vesselMstId]);

    useEffect(() => {
        if (
            mode === "Create" &&
            vesselMstId &&
            !hasAutoSavedPartA.current
        ) {
            hasAutoSavedPartA.current = true;
            autoCreatePartA();
        }
    }, [mode, vesselMstId, autoCreatePartA, fetchMonitoringData]);

    //Enable the download all required fileds entered
    const checkThetisEnable = async () => {
        if (!vesselMstId) return;
        try {
            const res = await callApi({
                method: "GET",
                url: API_ROUTES.GET_ENABLE_DOWNLOAD_XML(vesselMstId),
                data: {}
            });
            if (res?.isValid) {
                await handleDownloadXML()
            } else {
                emitToasterMsg("error", "XML", res?.errors)
            }
        } catch (err) {
            emitToasterMsg("error", "XML", err?.errors)
        }
    };

    //Download XML api
    const handleDownloadXML = async () => {
        if (!vesselMstId) return;
        try {
            const res = await callApi({
                method: "GET",
                url: API_ROUTES.GET_DOWNLOAD_FILE(vesselMstId),
                data: {},
                responseType: "blob",
            });
            if (res) {
                const url = window.URL.createObjectURL(new Blob([res]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `MRV-${vesselMstId}.xml`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                emitToasterMsg("success", "XML", "XML download successfully")
            }
        } catch (err) {
            console.error("Error downloading MRV XML", err);
        }
    };

    //Download pdf api integration
    const handleDownloadPDF = async () => {
        if (!vesselMstId) return;
        try {
            const res = await callApi({
                method: "POST",
                url: `${API_ROUTES.GET_DOWNLOAD_PDF(vesselMstId)}?planType=mrv`,
                data: {},
                responseType: "blob",
            });
            if (res) {
                const url = window.URL.createObjectURL(new Blob([res], { type: 'application/pdf' }));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `MRV-${vesselMstId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                emitToasterMsg("success", "PDF", "PDF downloaded successfully");
            }
        } catch (err) {
            console.error("Error downloading PDF", err);
            emitToasterMsg("error", "PDF", "Failed to download PDF");
        }
    };


    const breadcrumb = useMemo(
        () => [
            { label: "My Fleet", url: "/my-fleet", navId: "myfleet_navId" },
            {
                label: "MRV Monitoring Plan",
                url: "",
                navId: "mrv_monitoring_plan_navId",
            },
        ],
        []
    );

    const tabChangeHandler = (event) => {
        setActiveTab(event);
    };

    const navigate = useNavigate();
    const { deleteMonitoringPlan } = useMonitoringPlanActions(() => { });

    const handleDeleteClick = async () => {
        await deleteMonitoringPlan(vesselMstId);
        navigate("/plan-verification");
    };

    const handleDeleteBtnClick = () => {
        setConfirmDelete(true);
    };


    return (
        <>
            <div className="mb-2 d-flex justify-content-between details-container ml-4">
                <div className="ml-auto d-flex">
                    <>
                    {userHasFeature?.(FeatureType.MRVClone) && (
                        <Button
                            className="mr-2"
                            onClick={() => {
                                handleValidate("Mrv")
                            }}
                        >
                            EU MRV MP Clone
                        </Button>
                    )}
                        <Button
                            startIcon={<Icon icon={ICONS.DOWNLOAD} />}
                            className="mr-2"
                            onClick={checkThetisEnable}
                        >
                            Download XML
                        </Button>
                        <Button
                            startIcon={<Icon icon={ICONS.DOWNLOAD} />}
                            className="mr-2"
                            onClick={handleDownloadPDF}
                        >
                            Download PDF
                        </Button>

                        <Button
                            startIcon={
                                loading ? <Icon icon="loader" spin /> : <Icon icon="delete" />
                            }
                            disabled={!hasMrvPlan}
                            className="mr-2"
                            onClick={handleDeleteBtnClick}
                        >
                            Delete Monitoring Plan
                        </Button>
                    </>
                </div>
            </div>
            <div className="ml-4 mb-4">
                <Switch links={tabs} selected={selectedTab} onChange={tabChangeHandler} />

                {/* Tab Content */}
                {selectedTab === "MP PARTICULARS" && (
                    <MpParticulars mpParticularsData={monitoringData?.partASection} refetchMonitoringData={fetchMonitoringData} vesselMstId={vesselMstId} />
                )}

                {selectedTab === "BASIC DATA" && (
                    <BasicData
                        data={monitoringData?.partBSection}
                        onCreateVesselData={onCreateVesselData}
                        mode={mode}
                        dropdownData={allDropdownData}
                        refetchMonitoringData={fetchMonitoringData}
                        vesselMstId={vesselMstId}
                    />
                )}

                {selectedTab === "ACTIVITY DATA" && (
                    <ActivityData data={monitoringData?.partCSection} dropdownData={allDropdownData} refetchMonitoringData={fetchMonitoringData} b1Data={monitoringData.partBSection.b1ShipIdentification} vesselMstId={vesselMstId} />
                )}

                {selectedTab === "DATA GAPS" && (
                    <DataGaps
                        data={monitoringData?.partDSection}
                        dropdownData={allDropdownData}
                        refetchMonitoringData={fetchMonitoringData} vesselMstId={vesselMstId}
                    />
                )}

                {selectedTab === "MANAGEMENT" && (
                    <Management data={monitoringData?.partESection} dropdownData={allDropdownData} refetchMonitoringData={fetchMonitoringData} vesselMstId={vesselMstId} />
                )}

                {selectedTab === "FURTHER INFORMATION" && (
                    <FurtherInformation
                        data={monitoringData?.partFSection}
                        dropdownData={allDropdownData}
                        refetchMonitoringData={fetchMonitoringData}
                        vesselMstId={vesselMstId}
                    />
                )}
            </div>
            <div className="confirmation-modal-container">
                <Modal
                    isOpen={confirmDelete}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    onCloseClickListener={() => {
                        setConfirmDelete(false);
                    }}
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                onClick={handleDeleteClick}
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Delete
                            </Button>
                            <Button
                                className="modal-cancel-btn"
                                onClick={() => {
                                    setConfirmDelete(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <div>Are you sure you want to delete this MRV monitoring plan?</div>
                </Modal>
            </div>
            <div className="seemp-clone-modal-popup">
                <CloneModal
                    isOpen={showCloneModal}
                    headerTitle={"EU MRV MP Clone"}
                    vesselMstId={vesselMstId}
                    onClose={() => {
                        setShowCloneModal(false);
                        setSelectedSections([]);
                        setSelectedVessels([]);
                        setConfirmationChecked(false);
                    }}

                    sectionsList={sectionsList || []}

                    selectedSections={selectedSections}
                    setSelectedSections={setSelectedSections}

                    selectedVessels={selectedVessels}
                    setSelectedVessels={setSelectedVessels}

                    confirmationChecked={confirmationChecked}
                    setConfirmationChecked={setConfirmationChecked}

                    onClone={async () => {
                        const payload = {
                            "planType": cloneType,
                            "vesselMstId": vesselMstId,
                            "cloningOptionIds": [
                                ...selectedSections
                            ],
                            "targetVesselMstIds": selectedVessels?.map(v => v.vesselMstId),
                        }

                        // API Call Here
                        try {
                            const res = await callApi({
                                method: "POST",
                                url: API_ROUTES.CLONE_MRV(),
                                data: payload
                            });

                            if (res?.isSuccess) {
                                emitToasterMsg("success", "EU MRV MP Clone", "Cloned successfully");
                                setShowCloneModal(false);
                                setSelectedSections([]);
                                setSelectedVessels([]);
                                setConfirmationChecked(false);
                                setShowResultModal(true);
                                setResultSummaryData(res?.cloneSummaries || []);
                            }
                        } catch (err) {
                            emitToasterMsg("error", "EU MRV MP Clone", "Clone failed");
                        }
                    }}
                />
                <div className="clone-result-summary-modal-popup">
                    <ResultSummaryModal
                        isOpen={showResultModal}
                        onClose={() => setShowResultModal(false)}
                        resultSummaryData={resultSummaryData || []}
                        headerTitle={"EU MRV Clone - Result Summary"}
                        subHeaderTitle={"Clone result for selected target vessels"}
                        successMsg={"EU MRV"}
                    />
                </div>
            </div>
        </>
    );
}

export default MrvMonitoringPlan;